import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AudioLines, Lock, Play, Volume2 } from 'lucide-react';
import { Button } from 'components/ui/button';
import { cn } from 'lib/utils';
import * as Api from '../../api';
import { measureAudioDuration } from '../../audioDuration';
import { formatClock } from '../../constants';
import type { CandidateSection } from '../../types';

interface Props {
  attemptId: string;
  section: CandidateSection;
  /** Reports the recording now playing, so the questions can follow along. */
  onTrackChange?: (index: number) => void;
}

/** How often a playing single-play recording reports its position. */
const PROGRESS_EVERY_MS = 3000;

// 'blocked': the browser refused to start sound without a click (usual after a reload).
type TrackState = 'idle' | 'loading' | 'playing' | 'blocked' | 'ended' | 'error';

/**
 * How long a recording is. A browser reports Infinity (or NaN) for audio it
 * cannot measure up front — a streamed response with no length, some webm/ogg
 * files — and dividing by that leaves the bar at 0% and the total unreadable.
 * The length recorded at upload stands in until the browser knows the real one.
 */
const knownDuration = (el: HTMLAudioElement, uploaded?: number): number => {
  if (Number.isFinite(el.duration) && el.duration > 0) return el.duration;
  return uploaded && uploaded > 0 ? uploaded : 0;
};

/** The latest recording the server says was started, so a reload rejoins it. */
const lastStartedIndex = (tracks: Array<{ started: boolean }>) => tracks.reduce((found, track, i) => (track.started ? i : found), 0);

/**
 * IELTS Listening audio. SINGLE_PLAY: one Play button per track, no pause or
 * seek; after a reload it rejoins at the live position (the server records
 * when the track started). PRACTICE: ordinary controls.
 */
export function ListeningAudio({ attemptId, section, onTrackChange }: Props) {
  const { t } = useTranslation();
  const stimulus = section.stimulus;
  const singlePlay = stimulus?.playback !== 'PRACTICE';
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [current, setCurrent] = useState(() => lastStartedIndex(section.stimulus?.tracks ?? []));
  const [state, setState] = useState<TrackState>('idle');
  const [url, setUrl] = useState<string | null>(null);
  const [position, setPosition] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [volumeOpen, setVolumeOpen] = useState(false);
  // Where a single-play recording resumes: the server's record of how far it was heard.
  const resumeAtRef = useRef(0);
  const rejoinedRef = useRef(false);

  const tracks = stimulus?.tracks ?? [];
  const track = tracks[current];

  useEffect(() => {
    onTrackChange?.(current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current]);

  const start = async (index: number) => {
    setState('loading');
    setUrl(null);
    setDuration(0);
    try {
      const { data } = await Api.StartAudio(attemptId, section.id, index);
      if (data.ended || !data.url) {
        advanceFrom(index);
        return;
      }
      resumeAtRef.current = data.offsetSec;
      setUrl(data.url);
      setState('playing');
    } catch {
      setState('error');
    }
  };

  // Parts play back to back, as in the real test.
  const advanceFrom = (index: number) => {
    setUrl(null);
    setPosition(0);
    if (index + 1 < tracks.length) {
      setCurrent(index + 1);
      if (singlePlay) start(index + 1);
      else setState('idle');
    } else {
      setState('ended');
    }
  };

  // After a reload, rejoin the recording that was playing, once.
  useEffect(() => {
    if (rejoinedRef.current || !singlePlay || !track?.started) return;
    rejoinedRef.current = true;
    start(current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Play from where the candidate stopped hearing; a blocked autoplay waits for a click.
  // Time spent reloading does not count: the recording waits.
  const playFromResume = (el: HTMLAudioElement) => {
    if (singlePlay) {
      const at = resumeAtRef.current;
      if (el.duration && at >= el.duration) {
        advanceFrom(current);
        return;
      }
      if (at > 0.5 && Math.abs(el.currentTime - at) > 0.5) el.currentTime = at;
    }
    el.play()
      .then(() => setState('playing'))
      .catch((err: { name?: string }) => {
        if (err?.name === 'NotAllowedError') setState('blocked');
        else if (singlePlay) setState('error');
      });
  };

  useEffect(() => {
    const el = audioRef.current;
    if (!el || !url) return;
    el.volume = volume;
    let stale = false;
    const onLoaded = () => {
      const known = knownDuration(el, track?.durationSec);
      setDuration(known);
      // A browser-recorded WebM/OGG says Infinity, and one authored before lengths were
      // measured has none stored: read the length from the file so the bar can move.
      if (!known) {
        measureAudioDuration(url).then((length) => {
          if (!stale && length) setDuration(length);
        });
      }
      playFromResume(el);
    };
    if (el.readyState >= 1) onLoaded();
    else el.addEventListener('loadedmetadata', onLoaded, { once: true });
    return () => {
      stale = true;
      el.removeEventListener('loadedmetadata', onLoaded);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]);

  useEffect(() => {
    if (audioRef.current) audioRef.current.volume = volume;
  }, [volume]);

  const resume = () => {
    const el = audioRef.current;
    if (el && url) playFromResume(el);
    else start(current);
  };

  // Tell the server how far this recording has been heard, so a reload resumes there.
  const reportProgress = (seconds: number) => {
    if (!singlePlay) return;
    Api.AudioProgress(attemptId, section.id, current, Math.max(0, seconds)).catch(() => {
      // Best effort: the next report or the live position covers a miss.
    });
  };

  useEffect(() => {
    if (!singlePlay || state !== 'playing') return;
    const timer = window.setInterval(() => {
      const el = audioRef.current;
      if (el && !el.paused) reportProgress(el.currentTime);
    }, PROGRESS_EVERY_MS);
    return () => window.clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [singlePlay, state, current]);

  // Never erases a length already known: a later event may only say Infinity again.
  const onDurationChange = (e: React.SyntheticEvent<HTMLAudioElement>) => {
    const known = knownDuration(e.currentTarget, track?.durationSec);
    setDuration((was) => known || was);
  };

  const onEnded = () => {
    const el = audioRef.current;
    reportProgress(el ? knownDuration(el, duration) : duration);
    advanceFrom(current);
  };

  if (!stimulus || !tracks.length) return null;

  const progress = duration ? Math.min(100, (position / duration) * 100) : 0;
  const trackLabel = track?.label || t('exam.listening.part', { n: current + 1 });
  const clock = (
    <span className="whitespace-nowrap font-mono text-[13px] font-bold tabular-nums text-[#10182B]" aria-live="off">
      {formatClock(Math.floor(position))}
      {duration ? ` / ${formatClock(Math.floor(duration))}` : ''}
    </span>
  );
  const volumeSlider = (
    <input
      type="range"
      min={0}
      max={1}
      step={0.05}
      value={volume}
      onChange={(e) => setVolume(Number(e.target.value))}
      aria-label={t('exam.listening.volume')}
      className="w-24 accent-[#10182B] sm:w-28"
    />
  );
  const needsClick = singlePlay && (state === 'blocked' || state === 'idle' || state === 'error');
  const playLabel = state === 'blocked' ? t('exam.listening.resume') : state === 'error' ? t('exam.listening.retry') : t('exam.listening.play');

  return (
    <div className="shrink-0 border-b border-[#E6E9F2] bg-[#F4F6FB] px-3.5 py-2.5 text-slate-700 sm:px-6 sm:py-3">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 sm:flex-nowrap sm:gap-5">
        <span
          className={cn(
            'hidden h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[#E6E9F2] bg-white sm:flex',
            state === 'playing' && 'ring-4 ring-[#B42318]/15',
          )}
          aria-hidden
        >
          <AudioLines className="h-[18px] w-[18px] text-[#B42318]" />
        </span>

        <div className="min-w-0 basis-full sm:basis-0 sm:flex-1">
          <div className="flex items-center gap-2.5 text-[13px] sm:justify-between sm:gap-3">
            <span
              className={cn(
                'h-[9px] w-[9px] shrink-0 rounded-full sm:hidden',
                state === 'playing' ? 'bg-[#B42318] shadow-[0_0_0_4px_rgba(180,35,24,0.18)]' : 'bg-[#94A3B8]',
              )}
              aria-hidden
            />
            <span className="min-w-0 flex-1 truncate font-bold text-[#10182B] sm:flex-none">
              {trackLabel}
              {tracks.length > 1 && (
                <span className="ml-2 font-normal text-[#646D84]">
                  {t('exam.listening.track_of', {
                    n: current + 1,
                    total: tracks.length,
                  })}
                </span>
              )}
            </span>
            <Wave progress={progress} playing={state === 'playing'} />
            <span className="hidden sm:inline">{clock}</span>
            {singlePlay && state === 'playing' && (
              <span className="shrink-0 rounded-[7px] bg-[#FFF4D6] px-2 py-1 text-[11px] font-bold text-[#92400E] lg:hidden">
                {t('exam.listening.once_tag')}
              </span>
            )}
          </div>
          <div
            className="mt-2 h-1.5 overflow-hidden rounded-full bg-[#DDE2EF]"
            role="progressbar"
            aria-label={t('exam.listening.progress')}
            aria-valuenow={duration ? Math.round(progress) : undefined}
            aria-valuemin={0}
            aria-valuemax={100}
          >
            {duration || state !== 'playing' ? (
              <div className="h-full bg-[#10182B] transition-[width] duration-500" style={{ width: `${progress}%` }} />
            ) : (
              // Length unknown: show that it is playing instead of a bar stuck at 0%.
              <div className="h-full w-full bg-[#10182B]/30 motion-safe:animate-pulse" />
            )}
          </div>
          <div className="mt-1.5 flex items-center justify-between gap-3 sm:hidden">
            {clock}
            <div className="relative">
              <button
                type="button"
                onClick={() => setVolumeOpen((v) => !v)}
                aria-expanded={volumeOpen}
                aria-label={t('exam.listening.volume')}
                title={t('exam.listening.volume_help')}
                className="-my-1.5 flex h-11 w-11 items-center justify-center rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#10182B]"
              >
                <Volume2 className="h-5 w-5 text-[#4B5468]" aria-hidden />
              </button>
              {volumeOpen && (
                <div className="absolute right-0 top-11 z-20 rounded-lg border border-[#E6E9F2] bg-white px-3 py-2.5 shadow-lg">{volumeSlider}</div>
              )}
            </div>
          </div>
        </div>

        {needsClick ? (
          // A phone gives the click its own row, with the reason beside it when a reload stopped the audio.
          <div
            role={state === 'blocked' ? 'status' : undefined}
            className={cn(
              'flex basis-full items-center gap-3 max-[359px]:flex-wrap sm:contents',
              state === 'blocked' && 'rounded-lg bg-[#FFF4D6] py-1.5 pl-3 pr-1.5',
            )}
          >
            {state === 'blocked' && (
              <p className="min-w-0 flex-1 text-xs font-semibold leading-snug text-[#92400E] max-[359px]:basis-full sm:hidden">
                {t('exam.listening.resume_help_short')}
              </p>
            )}
            <Button
              size="sm"
              onClick={state === 'blocked' ? resume : () => start(current)}
              className={cn(
                'h-11 shrink-0 gap-1.5 whitespace-nowrap bg-primary px-3.5 text-[13px] text-[#0A1628] hover:bg-primary-300 sm:h-9',
                state === 'blocked' ? 'max-sm:flex-none max-[359px]:flex-1' : 'max-sm:flex-1',
              )}
            >
              <Play className="h-4 w-4" aria-hidden />
              {playLabel}
            </Button>
          </div>
        ) : singlePlay ? (
          <span className="hidden h-[30px] shrink-0 items-center gap-2 rounded-lg bg-[#FFF4D6] px-3 text-xs font-bold text-[#92400E] lg:inline-flex">
            {state === 'playing' ? <Lock className="h-3.5 w-3.5" aria-hidden /> : null}
            {state === 'loading' ? t('exam.listening.loading') : state === 'playing' ? t('exam.listening.no_seeking') : t('exam.listening.finished')}
          </span>
        ) : null}

        <label className="hidden shrink-0 items-center gap-2.5 text-xs text-[#646D84] sm:flex">
          <Volume2 className="h-4 w-4 text-[#4B5468]" aria-hidden />
          <span className="max-lg:sr-only">{t('exam.listening.volume')}</span>
          {volumeSlider}
        </label>
      </div>

      {singlePlay && state === 'blocked' && (
        <p className="mt-2 hidden text-xs font-semibold text-[#92400E] sm:block" role="status">
          {t('exam.listening.resume_help')}
        </p>
      )}

      {singlePlay ? (
        url && (
          // No controls: the candidate cannot pause or seek a single-play track.
          <audio
            ref={audioRef}
            src={url}
            preload="auto"
            onTimeUpdate={(e) => setPosition(e.currentTarget.currentTime)}
            onDurationChange={onDurationChange}
            onEnded={onEnded}
            onError={() => setState('error')}
          />
        )
      ) : (
        <div className="mt-3">
          {state === 'idle' && !url ? (
            <Button size="sm" onClick={() => start(current)} className="gap-1.5 bg-primary text-[#0A1628] hover:bg-primary-300">
              <Play className="h-4 w-4" aria-hidden /> {t('exam.listening.load_practice')}
            </Button>
          ) : (
            url && (
              <audio
                ref={audioRef}
                src={url}
                controls
                preload="auto"
                className="h-9 w-full"
                onTimeUpdate={(e) => setPosition(e.currentTarget.currentTime)}
                onDurationChange={onDurationChange}
                onEnded={onEnded}
              />
            )
          )}
          {tracks.length > 1 && (
            <div className="mt-2 flex flex-wrap gap-2">
              {tracks.map((tr) => (
                <button
                  key={tr.index}
                  type="button"
                  aria-pressed={tr.index === current}
                  onClick={() => {
                    setCurrent(tr.index);
                    setUrl(null);
                    setState('idle');
                    start(tr.index);
                  }}
                  className={cn(
                    'h-8 rounded-full px-3 text-xs font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#10182B]',
                    tr.index === current ? 'bg-[#10182B] text-white' : 'border border-[#E6E9F2] bg-white text-slate-700 hover:bg-slate-50',
                  )}
                >
                  {tr.label || t('exam.listening.part', { n: tr.index + 1 })}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/** Decorative level bars: the part already played is dark, like the design's recording strip. */
const WAVE = [8, 14, 22, 12, 26, 18, 10, 24, 16, 28, 12, 20, 9, 25, 15, 22, 11, 27, 17, 13, 23, 9, 19, 26, 12, 16, 21, 10];

function Wave({ progress, playing }: { progress: number; playing: boolean }) {
  const reached = Math.round((progress / 100) * WAVE.length);
  return (
    <div className="ml-auto mr-3 hidden h-7 items-center gap-[3px] xl:flex" aria-hidden>
      {WAVE.map((h, i) => (
        <i
          key={i}
          style={{ height: h }}
          className={cn(
            'block w-[3px] rounded-sm',
            i < reached ? 'bg-[#B42318]' : 'bg-[#CBD2E3]',
            playing && i === reached && 'motion-safe:animate-pulse',
          )}
        />
      ))}
    </div>
  );
}

export default ListeningAudio;
